import type {
  CourseProfessorStats,
  DepartmentCourseStats,
  DepartmentProfessorStats,
  OfferingRecord,
  ProfessorCourseRecord,
} from './types';

export type SortOrder = 'asc' | 'desc';
export type CourseOfferingSort = 'year' | 'score';
export type ProfessorCourseSort = 'year' | 'score';
export type DepartmentSort = 'name' | 'score' | 'students';

const DESCENDING_SEMESTER_ORDER: Record<string, number> = { Fall: 3, Spring: 2, Summer: 1 };
const ASCENDING_SEMESTER_ORDER: Record<string, number> = { Spring: 1, Summer: 2, Fall: 3 };

export function compareAcademicPeriods(
  left: Pick<OfferingRecord, 'year' | 'semester'>,
  right: Pick<OfferingRecord, 'year' | 'semester'>,
  order: SortOrder,
): number {
  const yearDifference = right.year - left.year;
  if (yearDifference !== 0) return order === 'desc' ? yearDifference : -yearDifference;
  return order === 'desc'
    ? (DESCENDING_SEMESTER_ORDER[right.semester] || 0) - (DESCENDING_SEMESTER_ORDER[left.semester] || 0)
    : (DESCENDING_SEMESTER_ORDER[left.semester] || 0) - (DESCENDING_SEMESTER_ORDER[right.semester] || 0);
}

export function sortCourseOfferings(
  offerings: OfferingRecord[],
  sortBy: CourseOfferingSort,
  order: SortOrder,
): OfferingRecord[] {
  return [...offerings].sort((left, right) => {
    if (sortBy === 'year') return compareAcademicPeriods(left, right, order);
    const difference = (right.overall_quality_score ?? 0) - (left.overall_quality_score ?? 0);
    return order === 'desc' ? difference : -difference;
  });
}

export function sortCourseProfessors(professors: CourseProfessorStats[]): CourseProfessorStats[] {
  return [...professors].sort((left, right) => right.avg_teaching_score - left.avg_teaching_score);
}

export function sortProfessorCourses(
  courses: ProfessorCourseRecord[],
  sortBy: ProfessorCourseSort,
  order: SortOrder,
): ProfessorCourseRecord[] {
  return [...courses].sort((left, right) => {
    if (sortBy === 'year') return compareAcademicPeriods(left, right, order);
    const difference = (right.teaching_effectiveness_score ?? 0) - (left.teaching_effectiveness_score ?? 0);
    return order === 'desc' ? difference : -difference;
  });
}

export function averageNonNull(values: Array<number | null | undefined>): number | null {
  const present = values.filter((value): value is number => value !== null && value !== undefined);
  return present.length > 0 ? present.reduce((sum, value) => sum + value, 0) / present.length : null;
}

export function courseDepartmentDelta(course: number | null, department: number | null): number | null {
  return course !== null && department !== null ? course - department : null;
}

export function responseRate(responses: number, enrollment: number): number {
  return (responses / enrollment) * 100;
}

export function thousandRoundedChartMaximum(values: number[]): number {
  const maximum = Math.max(...values, 0);
  return Math.ceil(maximum / 1000) * 1000;
}

export function niceChartMaximum(values: number[], steps = 5): number {
  const maximum = Math.max(...values, 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(maximum)));
  const normalizedMaximum = maximum / magnitude;
  const multiplier = normalizedMaximum <= 1 ? 1 : normalizedMaximum <= 2 ? 2 : normalizedMaximum <= 5 ? 5 : 10;
  return Math.max(multiplier * magnitude, steps);
}

export function compactSemesterLabel(label: string): string {
  return label.replace('Spring', 'Sp').replace('Fall', 'Fa').replace('Summer', 'Su');
}

export function uniqueNamedProfessorCourses(courses: ProfessorCourseRecord[]): ProfessorCourseRecord[] {
  return Array.from(
    new Map(
      courses
        .filter((course) => course.course_name && course.course_subject && course.course_code)
        .map((course) => [`${course.course_subject}:${course.course_code}`, course]),
    ).values(),
  ).sort((left, right) => (left.course_name || '').localeCompare(right.course_name || ''));
}

export function filterProfessorCourses(courses: ProfessorCourseRecord[], courseId: string): ProfessorCourseRecord[] {
  if (courseId === 'all') return courses;
  return courses.filter((course) => {
    const id = course.course_subject && course.course_code ? `${course.course_subject}:${course.course_code}` : null;
    return id === courseId;
  });
}

export interface EnrollmentPeriod {
  year: number;
  semester: string;
  enrollment: number;
}

export function aggregateEnrollmentByPeriod(courses: ProfessorCourseRecord[]): EnrollmentPeriod[] {
  const periods = new Map<string, EnrollmentPeriod>();
  courses.forEach((course) => {
    if (course.enrollment && course.year && course.semester) {
      const key = `${course.year}-${course.semester}`;
      const existing = periods.get(key);
      if (existing) existing.enrollment += course.enrollment;
      else periods.set(key, { year: course.year, semester: course.semester, enrollment: course.enrollment });
    }
  });
  return Array.from(periods.values()).sort((left, right) => {
    if (left.year !== right.year) return left.year - right.year;
    return (ASCENDING_SEMESTER_ORDER[left.semester] || 0) - (ASCENDING_SEMESTER_ORDER[right.semester] || 0);
  });
}

export function sortDepartmentCourses(courses: DepartmentCourseStats[], sortBy: DepartmentSort): DepartmentCourseStats[] {
  return [...courses].sort((left, right) => {
    if (Boolean(left.course_name) !== Boolean(right.course_name)) return left.course_name ? -1 : 1;
    if (sortBy === 'students') return (right.total_enrollment ?? 0) - (left.total_enrollment ?? 0);
    if (sortBy === 'score') return (right.avg_quality_score ?? 0) - (left.avg_quality_score ?? 0);
    return (left.course_name || left.course_code).localeCompare(right.course_name || right.course_code);
  });
}

export function sortDepartmentProfessors(professors: DepartmentProfessorStats[], sortBy: DepartmentSort): DepartmentProfessorStats[] {
  return [...professors].sort((left, right) => {
    if (sortBy === 'students') return (right.total_enrollment ?? 0) - (left.total_enrollment ?? 0);
    if (sortBy === 'score') return (right.avg_teaching_score ?? 0) - (left.avg_teaching_score ?? 0);
    return left.instructor_name.localeCompare(right.instructor_name);
  });
}

/** Hide survey rows with four or more unavailable comparison dimensions. */
export function hasUsefulSurvey(record: Partial<OfferingRecord>): boolean {
  const values = [record.Q1_section_mean, record.Q3_section_mean, record.Q7_section_mean, record.Q9_section_mean ?? record.teaching_effectiveness_score, record.Q10_section_mean ?? record.overall_quality_score];
  return values.filter((value) => typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 5).length >= 2;
}
